import React, { useState, useCallback, useMemo } from 'react';
import classnames from 'classnames';
import { useSelector } from 'react-redux';
import ConfirmationDialog from '../ConfirmationDialog';
import EditItemModal from '../EditItemModal';
import { DropdownMenu } from '../DropdownMenu';
import { getHasPermission } from '../../bundles/auth';

const ListItem = ({
  entity,
  item = {},
  itemIndex,
  columns = [],
  columnWidths = { type: 'percent', widths: [] },
  customEdit = false,
  isHeader,
  withSelect = false,
  withActions = true,
  extraMenuActions = [],
  onEdit = () => {
    // do something onEdit
  },
  onSubmit = () => {
    // do something onSubmit
  },
  onDelete = () => {
    // do something onDelete
  },
  onChange = () => {
    // do something onChange
  },
  onClose = () => {
    // do something onClose
  },
  onExpand = () => {
    // do something onExpand
  },
  isExpanded = false,
  schema = {},
  disableDefaultActions = false,
  withActionHeader = true,
  disableDelete = false,
  deletePermission = null,
  permissionScope = {},
  disableEdit = false,
  hideActionMenu = false,
  actionColumnWidth = 7,
  actionButtons = [],
  uiSchema = {},
  accordionContent = [],
  confirmationDialogTitle,
  confimationDialogBody,
  CustomEditModal,
  withTitle,
}) => {
  const [showEdit, setShowEdit] = useState(false);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [showAccordionContent, setShowAccordionContent] = useState(isExpanded);

  const hasDeletePermission = useSelector((state) =>
    deletePermission ? getHasPermission(state, deletePermission, permissionScope) : true
  );
  const isDeleteDisabled = disableDelete || !hasDeletePermission;

  const onEditItem = useCallback(() => {
    onEdit(item);
    if (!customEdit) {
      setShowEdit(true);
    }
  }, [item, customEdit]);

  const onFormSubmit = useCallback(
    ({ formData: data }) => {
      onSubmit(data);
      setShowEdit(false);
    },
    [onSubmit, setShowEdit]
  );

  const onFormChange = useCallback(
    ({ formData: data }) => {
      if (typeof onChange === 'function') onChange(data);
    },
    [onChange]
  );

  const onConfirmDelete = useCallback(() => {
    setShowConfirmDelete(false);
    onDelete();
  }, [setShowConfirmDelete, onDelete]);

  const onCloseEditModal = useCallback(() => {
    setShowEdit(false);
    onClose();
  }, [setShowEdit, onClose]);

  // 0.2rem margin-right on each col + action column width
  const totalMarginsAndActions =
    columns.length * 0.2 + (withActions ? actionColumnWidth : 0) + (withSelect ? 3.9 : 0);
  const colWidth = `calc((100% - ${totalMarginsAndActions}rem) / ${columns.length})`;
  const getCustomWidth = (index) => {
    if (columnWidths.type === 'percent') {
      if (index === 0 && withSelect) {
        return '3.9rem';
      }
      return `calc((100% - ${totalMarginsAndActions}rem) * (${columnWidths.widths[index]}/100))`;
    }
    if (columnWidths.type === 'rem') {
      if (columnWidths.widths[index] === 'fill') {
        const otherColsTotal = columnWidths.widths.reduce((acc, col) => {
          if (col !== 'fill') {
            return acc + col;
          }
          return acc;
        }, 0);

        return `calc(100% - ${totalMarginsAndActions}rem - ${otherColsTotal}rem)`;
      }
      return `${columnWidths.widths[index]}rem`;
    }
    return colWidth;
  };

  const hasAccordion = accordionContent?.length > 0;

  const classes = classnames({
    'list-item--clickable': hasAccordion,
    'list-item--header': isHeader,
  });

  const handleItemClick = () => {
    if (hasAccordion) {
      onExpand();
      setShowAccordionContent(!showAccordionContent);
    }
  };

  const itemMenuOptions = useMemo(() => {
    const options = [];

    if (extraMenuActions.some((action) => action.placement === 'before')) {
      options.push(
        ...extraMenuActions
          .filter((action) => action.placement === 'before')
          .map((action) => ({
            component: <div>{action.text}</div>,
            onSelect: action.action,
          }))
      );
    }

    const defaultOptions = [];
    if (!disableEdit) {
      defaultOptions.push({ component: <div>Edit</div>, onSelect: onEditItem });
    }

    if (!isDeleteDisabled) {
      defaultOptions.push({
        component: <div>Delete</div>,
        onSelect: () => setShowConfirmDelete(true),
      });
    }

    if (!disableDefaultActions) {
      options.push(...defaultOptions);
    }

    if (extraMenuActions) {
      options.push(
        ...extraMenuActions
          .filter((action) => action.placement !== 'before')
          .map((action) => ({
            component: <div>{action.text}</div>,
            onSelect: action.action,
          }))
      );
    }
    return hideActionMenu ? [] : options;
  }, [item, onEditItem, extraMenuActions, hideActionMenu]);

  const actionMenu = useMemo(
    () =>
      itemMenuOptions.length > 0 ? (
        <DropdownMenu
          trigger="he-moreoptionshorizontal"
          triggerSize="s"
          menuOptions={itemMenuOptions}
          className="list-item-menu"
          menuXPlacement="left"
          menuYPlacement="auto"
          menuShouldEscapeOverflow
          menuShouldBlockScroll
          styles={{
            dropdownIndicator: { padding: '0 0.5rem', filter: 'none' },
            option: { cursor: 'pointer' },
            menu: {
              minWidth: 'auto',
              zIndex: 2001,
            },
          }}
        />
      ) : null,
    [itemMenuOptions]
  );

  const actions = (
    <div
      className="actions"
      style={{ width: `${actionColumnWidth}rem` }}
    >
      {hideActionMenu ? actionButtons : actionMenu}
    </div>
  );
  const actionColumn = isHeader ? (
    <div
      className="list-item__col actions--header"
      style={{ width: `${actionColumnWidth}rem` }}
    >
      <div className="list-item--header__col">{withActionHeader ? 'Actions' : ''}</div>
    </div>
  ) : (
    actions
  );

  let editModal = (
    <EditItemModal
      entity={entity}
      action="Update"
      formData={item}
      schema={schema}
      uiSchema={uiSchema}
      onSubmit={onFormSubmit}
      onChange={onFormChange}
      onCloseModal={onCloseEditModal}
      withTitle={withTitle}
    />
  );
  if (CustomEditModal) {
    editModal = React.cloneElement(CustomEditModal, {
      onCloseModal: onCloseEditModal,
      integration: item,
    });
  }
  if (customEdit) {
    editModal = null;
  }

  return (
    <div
      data-testid="list-item"
      className={`list-item ${classes} `}
      onClick={handleItemClick}
    >
      {columns.map((c, index) => {
        const key = `list-item-${itemIndex}-column-${index}`;
        return (
          <div
            key={key}
            className={`list-item__col ${isHeader ? 'list-item--header__col' : ''}`}
            style={
              columnWidths.type === 'flex'
                ? { flex: columnWidths.widths[index] || '1' }
                : { width: columnWidths.widths[index] ? getCustomWidth(index) : colWidth }
            }
          >
            {c}
          </div>
        );
      })}

      {withActions && actionColumn}

      {showEdit ? editModal : null}

      {showConfirmDelete ? (
        <ConfirmationDialog
          onCancel={() => setShowConfirmDelete(false)}
          showCancel
          onConfirm={onConfirmDelete}
          title={confirmationDialogTitle}
          body={confimationDialogBody}
          confirmText="Remove"
          confirmType="danger"
        />
      ) : null}

      {showAccordionContent &&
        accordionContent.map((list, index_) => (
          <div
            className="list-item-accordion-content"
            key={index_}
          >
            {list.map((content, index) => (
              <React.Fragment key={`row-${index_}-col-${index}`}>
                <div
                  className="list-item__col"
                  style={
                    columnWidths.type === 'flex'
                      ? { flex: columnWidths.widths[index_] || '1' }
                      : { width: columnWidths.widths[index_] ? getCustomWidth(index_) : colWidth }
                  }
                >
                  {content}
                </div>
              </React.Fragment>
            ))}
            {withActions ? <div className="actions"></div> : null}
          </div>
        ))}
    </div>
  );
};

export default ListItem;
